import axios from 'axios';
import {
  User,
  Section,
  Block,
  Template,
  Contract,
  SignatureField,
  Signature,
  FormattedSection,
  AuthResponse,
  SystemSettings,
  Tenant,
  Notification,
  AuditLog,
  ApprovalWorkflow,
  SearchResult,
  ContractVersion,
  SystemVariable,
  Variable,
  HeaderFooterConfig,
  SignedDocument,
  DeletionLog,
  DocumentImportBlock,
  DocumentImportResult,
  ImportFormat
} from '../types';

// Determine API URL at runtime based on current location
const getApiUrl = (): string => {
  // If environment variable is set, use it
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // In browser, check if we're on localhost for development
  if (typeof window !== 'undefined') {
    const { hostname } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      // Development - check if we're on port 3000 (React dev server)
      if (window.location.port === '3000') {
        return 'http://localhost:3001/api';
      }
    }
  }
  
  // Production - use relative URL
  return '/api';
};

// Export function to get full API URL for direct browser access (PDF downloads, etc.)
export const getFullApiUrl = (path: string, includeAuth: boolean = false): string => {
  let baseUrl: string;
  
  // In development with React dev server
  if (typeof window !== 'undefined') {
    const { hostname, port } = window.location;
    if ((hostname === 'localhost' || hostname === '127.0.0.1') && port === '3000') {
      baseUrl = `http://localhost:3001/api${path}`;
    } else {
      // Production - use current origin with /api
      baseUrl = `${window.location.origin}/api${path}`;
    }
  } else {
    baseUrl = `/api${path}`;
  }
  
  // Add auth token to URL if requested (for downloads in new tab)
  if (includeAuth) {
    const token = localStorage.getItem('token');
    if (token) {
      const separator = baseUrl.includes('?') ? '&' : '?';
      baseUrl = `${baseUrl}${separator}token=${encodeURIComponent(token)}`;
    }
  }
  
  return baseUrl;
};

const API_URL = getApiUrl();
console.log('🔌 API URL:', API_URL);

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});
export const documentToolsApi = {
  previewQr: async (payload: unknown): Promise<{ content: string; dataUrl: string }> => (await api.post('/document-tools/qr/preview', { payload })).data,
  calculate: async (cells: Record<string,string|number|null>) => (await api.post('/document-tools/formula/calculate', { cells })).data,
  validate: async (document: unknown) => (await api.post('/document-tools/validate', { document })).data
};

// Request interceptor für Token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor für Fehlerbehandlung
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // A 401 from the login request itself means "wrong credentials" and must stay
    // on the page so the form can render the error instead of reloading it away.
    const isLoginRequest = error.config?.url?.includes('/auth/login');
    if (error.response?.status === 401 && !isLoginRequest) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: async (data: { email: string; password: string }): Promise<AuthResponse> => {
    console.log('🔐 Attempting login to:', API_URL + '/auth/login');
    try {
      const response = await api.post('/auth/login', data);
      console.log('✅ Login successful');
      return response.data;
    } catch (error: any) {
      console.error('❌ Login failed:', error.message);
      console.error('Response:', error.response?.data);
      throw error;
    }
  },

  getMe: async (): Promise<{ user: User }> => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  changePassword: async (data: {
    currentPassword: string;
    newPassword: string;
  }): Promise<{ message: string }> => {
    const response = await api.put('/auth/change-password', data);
    return response.data;
  }
};

// Settings API
export const settingsApi = {
  get: async (): Promise<{ settings: SystemSettings }> => {
    const response = await api.get('/settings');
    return response.data;
  },

  update: async (data: Partial<SystemSettings>): Promise<{ message: string; settings: SystemSettings }> => {
    const response = await api.put('/settings', data);
    return response.data;
  },

  testEmail: async (testEmail: string): Promise<{ message: string }> => {
    const response = await api.post('/settings/test-email', { testEmail });
    return response.data;
  },

  testATrust: async (): Promise<{ message: string; info: string }> => {
    const response = await api.post('/settings/test-atrust');
    return response.data;
  },

  getDeletionLog: async (params?: { page?: number; limit?: number; entityType?: string }): Promise<{
    logs: DeletionLog[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> => {
    const response = await api.get('/settings/deletion-log', { params });
    return response.data;
  },

  getDeletionLogEntry: async (id: string): Promise<{ log: DeletionLog }> => {
    const response = await api.get(`/settings/deletion-log/${id}`);
    return response.data;
  },

  // FTP Settings
  getFtp: async (): Promise<{
    enabled: boolean;
    localMode: boolean;
    host: string;
    port: number;
    user: string;
    password: string;
    inboxPath: string;
    archivePath: string;
    connectionInfo: {
      host: string;
      port: number;
      user: string;
      password: string;
      inboxPath: string;
      archivePath: string;
    };
  }> => {
    const response = await api.get('/settings/ftp');
    return response.data;
  },

  updateFtp: async (data: {
    ftpEnabled?: boolean;
    ftpLocalMode?: boolean;
    ftpHost?: string;
    ftpPort?: number;
    ftpUser?: string;
    ftpPassword?: string;
    ftpInboxPath?: string;
    ftpArchivePath?: string;
    ftpScanInterval?: number;
  }): Promise<{ message: string; settings: any }> => {
    const response = await api.put('/settings/ftp', data);
    return response.data;
  },

  scanFtp: async (): Promise<{ message: string; results: any[] }> => {
    const response = await api.post('/settings/ftp/scan');
    return response.data;
  }
};

// Sections API
export const sectionsApi = {
  getAll: async (): Promise<{ sections: Section[] }> => {
    const response = await api.get('/sections');
    return response.data;
  },

  getById: async (id: string): Promise<{ section: Section & { blocks: Block[] } }> => {
    const response = await api.get(`/sections/${id}`);
    return response.data;
  },

  create: async (data: {
    name: string;
    description?: string;
    sortOrder?: number;
  }): Promise<{ message: string; section: Section }> => {
    const response = await api.post('/sections', data);
    return response.data;
  },

  update: async (
    id: string,
    data: { name?: string; description?: string; sortOrder?: number }
  ): Promise<{ message: string; section: Section }> => {
    const response = await api.put(`/sections/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/sections/${id}`);
    return response.data;
  },

  reorder: async (sections: { id: string; sortOrder: number }[]): Promise<{ message: string }> => {
    const response = await api.put('/sections/reorder', { sections });
    return response.data;
  }
};

// Blocks API
export const blocksApi = {
  getAll: async (params?: { sectionId?: string; active?: boolean }): Promise<{ blocks: Block[] }> => {
    const response = await api.get('/blocks', { params });
    return response.data;
  },

  getById: async (id: string): Promise<{ block: Block }> => {
    const response = await api.get(`/blocks/${id}`);
    return response.data;
  },

  create: async (data: {
    name: string;
    content: string;
    documentJson?: string | null;
    sectionId?: string | null;
    isActive?: boolean;
    variables?: Partial<Variable>[];
  }): Promise<{ message: string; block: Block; detectedVariables: string[] }> => {
    const response = await api.post('/blocks', data);
    return response.data;
  },

  update: async (id: string, data: any): Promise<{ message: string; block: Block }> => {
    const response = await api.put(`/blocks/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/blocks/${id}`);
    return response.data;
  },

  extractVariables: async (content: string): Promise<{ variables: string[] }> => {
    const response = await api.post('/blocks/extract-variables', { content });
    return response.data;
  },

  importCsv: async (csvData: string): Promise<{ 
    message: string; 
    results: { line: number; name: string; success: boolean; error?: string; blockId?: string }[] 
  }> => {
    const response = await api.post('/blocks/import', { csvData });
    return response.data;
  },

  downloadImportExample: (): string => {
    return `${API_URL}/blocks/import/example`;
  },

  /** Datei auswerten, ohne etwas zu speichern (Base64, damit auch Word-Dateien unverändert ankommen). */
  previewDocument: async (data: string, options: { fileName?: string; sectionId?: string | null }): Promise<{
    format: ImportFormat;
    blocks: DocumentImportBlock[];
    warnings: string[];
  }> => {
    const response = await api.post('/blocks/import-document/preview', {
      data,
      fileName: options.fileName,
      sectionId: options.sectionId || null
    });
    return response.data;
  },

  /** Bauteile aus einer formatierten Datei anlegen. */
  importDocument: async (data: string, options: { fileName?: string; sectionId?: string | null; skipExisting?: boolean }): Promise<{
    message: string;
    format: ImportFormat;
    results: DocumentImportResult[];
    warnings: string[];
  }> => {
    const response = await api.post('/blocks/import-document', {
      data,
      fileName: options.fileName,
      sectionId: options.sectionId || null,
      skipExisting: options.skipExisting === true
    });
    return response.data;
  },

  downloadDocumentExample: async (format: ImportFormat): Promise<string> => {
    const response = await api.get('/blocks/import-document/example', {
      params: { format },
      responseType: 'text',
      transformResponse: value => value
    });
    return response.data as string;
  }
};

// Templates API
export const templatesApi = {
  getAll: async (params?: { active?: boolean }): Promise<{ templates: Template[] }> => {
    const response = await api.get('/templates', { params });
    return response.data;
  },

  getById: async (id: string): Promise<{ template: Template }> => {
    const response = await api.get(`/templates/${id}`);
    return response.data;
  },

  create: async (data: {
    name: string;
    description?: string;
    blocks?: { blockId: string; sortOrder?: number }[];
  }): Promise<{ message: string; template: Template }> => {
    const response = await api.post('/templates', data);
    return response.data;
  },

  update: async (id: string, data: any): Promise<{ message: string; template: Template }> => {
    const response = await api.put(`/templates/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/templates/${id}`);
    return response.data;
  },

  duplicate: async (id: string, name?: string): Promise<{ message: string; template: Template }> => {
    const response = await api.post(`/templates/${id}/duplicate`, { name });
    return response.data;
  },
  getVersions: async (id:string) => (await api.get(`/templates/${id}/versions`)).data,
  exportPackage: async (id:string) => (await api.get(`/templates/${id}/export`)).data,
  importPackage: async (data:unknown) => (await api.post('/templates/import/package',data)).data
};

// Contracts API
export const contractsApi = {
  getAll: async (params?: { status?: string; mine?: boolean; includeArchived?: boolean }): Promise<{ contracts: Contract[] }> => {
    const response = await api.get('/contracts', { params });
    return response.data;
  },

  getById: async (id: string): Promise<{ contract: Contract }> => {
    const response = await api.get(`/contracts/${id}`);
    return response.data;
  },

  create: async (data: {
    title: string;
    templateId?: string;
    blocks?: { blockId: string; sortOrder?: number }[];
    expirationDate?: string;
    isUnlimited?: boolean;
  }): Promise<{ message: string; contract: Contract }> => {
    const response = await api.post('/contracts', data);
    return response.data;
  },

  updateExpiration: async (id: string, data: {
    expirationDate?: string | null;
    isUnlimited?: boolean;
  }): Promise<{ message: string; contract: Contract }> => {
    const response = await api.put(`/contracts/${id}/expiration`, data);
    return response.data;
  },

  updateBlocks: async (
    id: string,
    blocks: { blockId: string; sortOrder?: number; customContent?: string; systemVariableId?: string }[]
  ): Promise<{ message: string; contract: Contract }> => {
    const response = await api.put(`/contracts/${id}/blocks`, { blocks });
    return response.data;
  },

  proceedToVariables: async (id: string): Promise<{ message: string }> => {
    const response = await api.post(`/contracts/${id}/proceed-to-variables`);
    return response.data;
  },

  saveVariablesDraft: async (
    id: string,
    variableValues: Record<string, Record<string, string>>
  ): Promise<{ message: string }> => {
    const response = await api.put(`/contracts/${id}/variables/draft`, { variableValues });
    return response.data;
  },

  finalizeVariables: async (
    id: string,
    variableValues: Record<string, Record<string, string>>
  ): Promise<{ message: string }> => {
    const response = await api.put(`/contracts/${id}/variables/finalize`, { variableValues });
    return response.data;
  },

  submitSignature: async (id: string): Promise<{ message: string }> => {
    const response = await api.post(`/contracts/${id}/submit-signature`);
    return response.data;
  },
  finalizeDocument: async(id:string):Promise<{message:string;hash:string;frozenAt:string}> => (await api.post(`/contracts/${id}/finalize-document`)).data,
  updateDocument: async(id:string,document:unknown,revision:number) => (await api.put(`/contracts/${id}/document`,{document,revision})).data,

  submitReview: async (id: string): Promise<{ message: string }> => {
    const response = await api.post(`/contracts/${id}/submit-review`);
    return response.data;
  },

  updateSignatureFields: async (
    id: string,
    signatureFields: Partial<SignatureField>[]
  ): Promise<{ message: string; signatureFields: SignatureField[] }> => {
    const response = await api.put(`/contracts/${id}/signature-fields`, { signatureFields });
    return response.data;
  },

  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/contracts/${id}`);
    return response.data;
  },

  getFormatted: async (id: string): Promise<{
    contract: { id: string; title: string; status: string; version: number };
    formattedSections: FormattedSection[];
  }> => {
    const response = await api.get(`/contracts/${id}/formatted`);
    return response.data;
  },

  createNewVersion: async (id: string): Promise<{ message: string; contract: Contract }> => {
    const response = await api.post(`/contracts/${id}/new-version`);
    return response.data;
  },

  getVersions: async (id: string): Promise<{ versions: ContractVersion[] }> => {
    const response = await api.get(`/contracts/${id}/versions`);
    return response.data;
  },

  updateHeaderFooter: async (
    id: string,
    config: { headerConfig?: HeaderFooterConfig; footerConfig?: HeaderFooterConfig }
  ): Promise<{
    message: string;
    headerConfig: HeaderFooterConfig | null;
    footerConfig: HeaderFooterConfig | null;
  }> => {
    const response = await api.put(`/contracts/${id}/header-footer`, config);
    return response.data;
  },

  updateHeadline: async (
    id: string,
    headline: string
  ): Promise<{ message: string; headline: string }> => {
    const response = await api.put(`/contracts/${id}/headline`, { headline });
    return response.data;
  },

  resetStatus: async (
    id: string,
    targetStatus: string = 'DRAFT',
    targetPhase: string = 'BLOCKS'
  ): Promise<{ message: string; contract: Contract }> => {
    const response = await api.post(`/contracts/${id}/reset-status`, { targetStatus, targetPhase });
    return response.data;
  },

  archive: async (id: string): Promise<{ message: string; contract: Contract }> => {
    const response = await api.post(`/contracts/${id}/archive`);
    return response.data;
  },

  unarchive: async (id: string): Promise<{ message: string; contract: Contract }> => {
    const response = await api.post(`/contracts/${id}/unarchive`);
    return response.data;
  },

  permanentDelete: async (
    id: string,
    confirmation: string,
    reason?: string
  ): Promise<{ message: string }> => {
    const response = await api.delete(`/contracts/${id}/permanent`, {
      data: { confirmation, reason }
    });
    return response.data;
  }
};

// Signatures API
export const signaturesApi = {
  sign: async (data: {
    signatureFieldId: string;
    signatureData: string;
    signatureType?: string;
  }): Promise<{ message: string; signature: Partial<Signature>; contractFullySigned: boolean }> => {
    const response = await api.post('/signatures', data);
    return response.data;
  },

  verify: async (id: string): Promise<{
    signature: Partial<Signature>;
    verification: { hashValid: boolean; hashAlgorithm: string; hasCertificate: boolean; hasTimestamp: boolean };
  }> => {
    const response = await api.get(`/signatures/${id}/verify`);
    return response.data;
  },

  getByContract: async (contractId: string): Promise<{ signatures: Signature[] }> => {
    const response = await api.get(`/signatures/contract/${contractId}`);
    return response.data;
  },

  getAuditLog: async (contractId: string): Promise<{ contract: any; auditLog: any[] }> => {
    const response = await api.get(`/signatures/audit/${contractId}`);
    return response.data;
  }
};

// Approvals API
export const approvalsApi = {
  createWorkflow: async (
    contractId: string,
    steps: { approverEmail: string; approverName?: string; dueDate?: string }[]
  ): Promise<{ message: string; workflow: ApprovalWorkflow }> => {
    const response = await api.post(`/approvals/contract/${contractId}`, { steps });
    return response.data;
  },

  getWorkflow: async (contractId: string): Promise<{ workflow: ApprovalWorkflow }> => {
    const response = await api.get(`/approvals/contract/${contractId}`);
    return response.data;
  },

  approve: async (stepId: string, comment?: string): Promise<{ message: string }> => {
    const response = await api.post(`/approvals/step/${stepId}/action`, { action: 'APPROVE', comment });
    return response.data;
  },

  reject: async (stepId: string, comment: string): Promise<{ message: string }> => {
    const response = await api.post(`/approvals/step/${stepId}/action`, { action: 'REJECT', comment });
    return response.data;
  },

  addComment: async (stepId: string, comment: string): Promise<{ message: string }> => {
    const response = await api.post(`/approvals/step/${stepId}/comment`, { comment });
    return response.data;
  },

  getPending: async (): Promise<{ pendingApprovals: any[] }> => {
    const response = await api.get('/approvals/pending');
    return response.data;
  }
};

// Notifications API
export const notificationsApi = {
  getAll: async (params?: { unreadOnly?: boolean; limit?: number }): Promise<{
    notifications: Notification[];
    total: number;
    unreadCount: number;
  }> => {
    const response = await api.get('/notifications', { params });
    return response.data;
  },

  markAsRead: async (id: string): Promise<{ message: string }> => {
    const response = await api.put(`/notifications/${id}/read`);
    return response.data;
  },

  markAllAsRead: async (): Promise<{ message: string }> => {
    const response = await api.put('/notifications/read-all');
    return response.data;
  },

  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/notifications/${id}`);
    return response.data;
  }
};

// Tenants API
export const tenantsApi = {
  getAll: async (): Promise<{ enabled: boolean; tenants: Tenant[]; message?: string }> => {
    const response = await api.get('/tenants');
    return response.data;
  },

  getById: async (id: string): Promise<{ tenant: Tenant }> => {
    const response = await api.get(`/tenants/${id}`);
    return response.data;
  },

  create: async (data: { name: string; slug: string; settings?: any }): Promise<{ message: string; tenant: Tenant }> => {
    const response = await api.post('/tenants', data);
    return response.data;
  },

  update: async (id: string, data: any): Promise<{ message: string; tenant: Tenant }> => {
    const response = await api.put(`/tenants/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/tenants/${id}`);
    return response.data;
  },

  assignUser: async (tenantId: string, userId: string): Promise<{ message: string }> => {
    const response = await api.post(`/tenants/${tenantId}/users`, { userId });
    return response.data;
  }
};

// Audit API
export const auditApi = {
  getAll: async (params?: {
    limit?: number;
    offset?: number;
    userId?: string;
    entityType?: string;
    action?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<{ logs: AuditLog[]; total: number }> => {
    const response = await api.get('/audit', { params });
    return response.data;
  },

  getForEntity: async (entityType: string, entityId: string): Promise<{ logs: AuditLog[] }> => {
    const response = await api.get(`/audit/${entityType}/${entityId}`);
    return response.data;
  },

  getContractHistory: async (contractId: string): Promise<{ history: any[] }> => {
    const response = await api.get(`/audit/contract/${contractId}/history`);
    return response.data;
  }
};

// Search API
export const searchApi = {
  search: async (params: {
    q: string;
    type?: 'contracts' | 'templates' | 'blocks';
    status?: string;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
  }): Promise<SearchResult> => {
    const response = await api.get('/search', { params });
    return response.data;
  },

  advancedContractSearch: async (data: {
    title?: string;
    status?: string | string[];
    createdBy?: string;
    templateId?: string;
    dateFrom?: string;
    dateTo?: string;
    hasSignatures?: boolean;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
  }): Promise<{
    contracts: Contract[];
    pagination: { total: number; limit: number; offset: number; hasMore: boolean };
  }> => {
    const response = await api.post('/search/contracts/advanced', data);
    return response.data;
  }
};

// PDF API
export const pdfApi = {
  getUrl: (contractId: string, download = false): string => {
    return `${API_URL}/pdf/${contractId}?download=${download}`;
  },

  getPreview: async (contractId: string): Promise<{
    contractId: string;
    title: string;
    status: string;
    blockCount: number;
    signatureFieldCount: number;
    pdfUrl: string;
    downloadUrl: string;
  }> => {
    const response = await api.get(`/pdf/${contractId}/preview`);
    return response.data;
  }
};

// System Variables API
export const systemVariablesApi = {
  getAll: async (): Promise<{ variables: SystemVariable[] }> => {
    const response = await api.get('/system-variables');
    return response.data;
  },

  getGrouped: async (): Promise<{ categories: { category: string; variables: SystemVariable[] }[] }> => {
    const response = await api.get('/system-variables/grouped');
    return response.data;
  },

  getById: async (id: string): Promise<{ variable: SystemVariable }> => {
    const response = await api.get(`/system-variables/${id}`);
    return response.data;
  },

  resolve: async (contractId: string, variableId: string): Promise<{ value: string }> => {
    const response = await api.get(`/system-variables/resolve/${contractId}/${variableId}`);
    return response.data;
  },

  resolveAll: async (contractId: string): Promise<{ variables: (SystemVariable & { value: string })[] }> => {
    const response = await api.get(`/system-variables/resolve-all/${contractId}`);
    return response.data;
  },

  search: async (query: string): Promise<{ results: SystemVariable[] }> => {
    const response = await api.get(`/system-variables/search/${query}`);
    return response.data;
  }
};

// Setup API (für initiale Einrichtung)
export const setupApi = {
  getStatus: async (): Promise<{ setupRequired: boolean; message: string }> => {
    const response = await api.get('/setup/status');
    return response.data;
  },

  createAdmin: async (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    companyName?: string;
  }): Promise<{
    message: string;
    user: User;
    token: string;
  }> => {
    const response = await api.post('/setup/create-admin', data);
    return response.data;
  }
};

// Signed Documents API (Archiv-Dokumente)
export const signedDocumentsApi = {
  // Alle Dokumente abrufen
  getAll: async (params?: { unassigned?: boolean }): Promise<{ documents: SignedDocument[] }> => {
    const response = await api.get('/signed-documents', { params });
    return response.data;
  },

  getByContract: async (contractId: string): Promise<{ documents: SignedDocument[] }> => {
    const response = await api.get(`/signed-documents/contract/${contractId}`);
    return response.data;
  },

  getById: async (id: string): Promise<{ document: SignedDocument }> => {
    const response = await api.get(`/signed-documents/${id}`);
    return response.data;
  },

  // Download URL mit Auth-Token
  getDownloadUrl: (id: string): string => {
    const token = localStorage.getItem('token');
    const baseUrl = api.defaults.baseURL || '/api';
    return `${baseUrl}/signed-documents/${id}/download${token ? `?token=${encodeURIComponent(token)}` : ''}`;
  },

  // View URL für Inline-Anzeige mit Auth-Token
  getViewUrl: (id: string): string => {
    const token = localStorage.getItem('token');
    const baseUrl = api.defaults.baseURL || '/api';
    return `${baseUrl}/signed-documents/${id}/view${token ? `?token=${encodeURIComponent(token)}` : ''}`;
  },

  download: (id: string): string => {
    return `${api.defaults.baseURL}/signed-documents/${id}/download`;
  },

  upload: async (contractId: string, file: File, notes?: string): Promise<{ 
    message: string; 
    document: SignedDocument 
  }> => {
    const formData = new FormData();
    formData.append('file', file);
    if (notes) formData.append('notes', notes);
    
    // Don't set Content-Type header - let browser set it with boundary
    const response = await api.post(`/signed-documents/upload/${contractId}`, formData);
    return response.data;
  },

  // Upload mit automatischem QR-Code Scan
  uploadWithScan: async (file: File, contractId?: string, notes?: string): Promise<{ 
    message: string; 
    document: SignedDocument;
    matchedByQr: boolean;
    contractId: string | null;
  }> => {
    const formData = new FormData();
    // Ensure proper filename with .pdf extension
    const fileName = file.name.endsWith('.pdf') ? file.name : `${file.name}.pdf`;
    formData.append('file', file, fileName);
    if (contractId) formData.append('contractId', contractId);
    if (notes) formData.append('notes', notes);
    
    // Don't set Content-Type header - let browser set it with boundary
    const response = await api.post('/signed-documents/upload-with-scan', formData);
    return response.data;
  },

  // Dokument einem Vertrag zuordnen
  assign: async (id: string, contractId: string | null): Promise<{ message: string; document: SignedDocument }> => {
    const response = await api.put(`/signed-documents/${id}/assign`, { contractId });
    return response.data;
  },

  bulkUpload: async (files: File[]): Promise<{
    message: string;
    results: { filename: string; success: boolean; contractId?: string; error?: string }[];
  }> => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    
    // Don't set Content-Type header - let browser set it with boundary
    const response = await api.post('/signed-documents/bulk-upload', formData);
    return response.data;
  },

  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/signed-documents/${id}`);
    return response.data;
  },

  // Archive Robot
  getRobotStatus: async (): Promise<{
    enabled: boolean;
    host: string | null;
    inboxPath: string;
    archivePath: string;
    scanInterval: number;
  }> => {
    const response = await api.get('/signed-documents/robot/status');
    return response.data;
  },

  triggerRobotScan: async (): Promise<{
    message: string;
    results: { filename: string; contractId: string | null; success: boolean; error?: string }[];
  }> => {
    const response = await api.post('/signed-documents/robot/scan');
    return response.data;
  }
};

// Users API (Benutzerverwaltung)
export const usersApi = {
  getAll: async (params?: { role?: string; active?: boolean; search?: string }): Promise<{ users: any[] }> => {
    const response = await api.get('/users', { params });
    return response.data;
  },

  getById: async (id: string): Promise<{ user: any }> => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },

  create: async (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    role: string;
    tenantId?: string;
  }): Promise<{ message: string; user: any }> => {
    const response = await api.post('/users', data);
    return response.data;
  },

  update: async (id: string, data: {
    email?: string;
    firstName?: string;
    lastName?: string;
    role?: string;
    isActive?: boolean;
    tenantId?: string;
  }): Promise<{ message: string; user: any }> => {
    const response = await api.put(`/users/${id}`, data);
    return response.data;
  },

  resetPassword: async (id: string, newPassword: string): Promise<{ message: string }> => {
    const response = await api.put(`/users/${id}/reset-password`, { newPassword });
    return response.data;
  },

  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  },

  toggleActive: async (id: string): Promise<{ message: string; user: any }> => {
    const response = await api.put(`/users/${id}/toggle-active`);
    return response.data;
  }
};

export default api;
